import { gql, DocumentNode } from '@apollo/client';
import { ITEM_FRAGMENT } from './items';

// ── Payments ──

export const GET_PAYMENTS = gql`
  query Payments($itemModelId: Int!, $filter: JSON, $limit: Int, $offset: Int) {
    payments(
      itemModelId: $itemModelId
      filter: $filter
      limit: $limit
      offset: $offset
    ) {
      count
      results {
        ...ItemFragment
      }
      globalActions
      deletePermission
    }
  }
  ${ITEM_FRAGMENT}
`;

export const GET_PAYMENT = gql`
  query Payment($id: Int!) {
    payment(id: $id) {
      ...ItemFragment
    }
  }
  ${ITEM_FRAGMENT}
`;

export const CREATE_PAYMENT = gql`
  mutation CreatePayment($input: CreatePaymentInput!) {
    createPayment(input: $input) {
      ...ItemFragment
    }
  }
  ${ITEM_FRAGMENT}
`;

export const UPDATE_PAYMENT = gql`
  mutation UpdatePayment($input: UpdatePaymentInput!) {
    updatePayment(input: $input) {
      ...ItemFragment
    }
  }
  ${ITEM_FRAGMENT}
`;

export const DELETE_PAYMENT = gql`
  mutation DeletePayment($id: Int!) {
    deletePayment(id: $id)
  }
`;

// ── Rates ──

export const GET_RATES = gql`
  query Rates($itemModelId: Int!, $filter: JSON, $limit: Int, $offset: Int) {
    rates(
      itemModelId: $itemModelId
      filter: $filter
      limit: $limit
      offset: $offset
    ) {
      count
      results {
        ...ItemFragment
      }
      globalActions
      deletePermission
    }
  }
  ${ITEM_FRAGMENT}
`;

export const GET_RATE = gql`
  query Rate($id: Int!) {
    rate(id: $id) {
      ...ItemFragment
    }
  }
  ${ITEM_FRAGMENT}
`;

export const CREATE_RATE = gql`
  mutation CreateRate($input: CreateRateInput!) {
    createRate(input: $input) {
      ...ItemFragment
    }
  }
  ${ITEM_FRAGMENT}
`;

export const UPDATE_RATE = gql`
  mutation UpdateRate($input: UpdateRateInput!) {
    updateRate(input: $input) {
      ...ItemFragment
    }
  }
  ${ITEM_FRAGMENT}
`;

export const DELETE_RATE = gql`
  mutation DeleteRate($id: Int!) {
    deleteRate(id: $id)
  }
`;

// ── Paychecks ──

export const GET_PAYCHECKS = gql`
  query Paychecks($itemModelId: Int!, $filter: JSON, $limit: Int, $offset: Int) {
    paychecks(
      itemModelId: $itemModelId
      filter: $filter
      limit: $limit
      offset: $offset
    ) {
      count
      results {
        ...ItemFragment
      }
      globalActions
      deletePermission
    }
  }
  ${ITEM_FRAGMENT}
`;

export const GET_PAYCHECK = gql`
  query Paycheck($id: Int!) {
    paycheck(id: $id) {
      ...ItemFragment
    }
  }
  ${ITEM_FRAGMENT}
`;

export const CREATE_PAYCHECK = gql`
  mutation CreatePaycheck($input: CreatePaycheckInput!) {
    createPaycheck(input: $input) {
      ...ItemFragment
    }
  }
  ${ITEM_FRAGMENT}
`;

export const UPDATE_PAYCHECK = gql`
  mutation UpdatePaycheck($input: UpdatePaycheckInput!) {
    updatePaycheck(input: $input) {
      ...ItemFragment
    }
  }
  ${ITEM_FRAGMENT}
`;

export const DELETE_PAYCHECK = gql`
  mutation DeletePaycheck($id: Int!) {
    deletePaycheck(id: $id)
  }
`;

// ── Helpers ──

type PayrollDataType = 'payments' | 'rates' | 'paycheck';

const PAYROLL_DATA_TYPES: PayrollDataType[] = ['payments', 'rates', 'paycheck'];

export const isPayrollDataType = (
  dataType?: string,
): dataType is PayrollDataType =>
  !!dataType && PAYROLL_DATA_TYPES.includes(dataType as PayrollDataType);

const listQueries: Record<PayrollDataType, DocumentNode> = {
  payments: GET_PAYMENTS,
  rates: GET_RATES,
  paycheck: GET_PAYCHECKS,
};

const createMutations: Record<PayrollDataType, DocumentNode> = {
  payments: CREATE_PAYMENT,
  rates: CREATE_RATE,
  paycheck: CREATE_PAYCHECK,
};

const updateMutations: Record<PayrollDataType, DocumentNode> = {
  payments: UPDATE_PAYMENT,
  rates: UPDATE_RATE,
  paycheck: UPDATE_PAYCHECK,
};

const deleteMutations: Record<PayrollDataType, DocumentNode> = {
  payments: DELETE_PAYMENT,
  rates: DELETE_RATE,
  paycheck: DELETE_PAYCHECK,
};

const listQueryNames: Record<PayrollDataType, string> = {
  payments: 'Payments',
  rates: 'Rates',
  paycheck: 'Paychecks',
};

const listRootFields: Record<PayrollDataType, string> = {
  payments: 'payments',
  rates: 'rates',
  paycheck: 'paychecks',
};

export const getPayrollListQuery = (dataType?: string): DocumentNode | null =>
  isPayrollDataType(dataType) ? listQueries[dataType] : null;

export const getPayrollCreateMutation = (dataType?: string): DocumentNode | null =>
  isPayrollDataType(dataType) ? createMutations[dataType] : null;

export const getPayrollUpdateMutation = (dataType?: string): DocumentNode | null =>
  isPayrollDataType(dataType) ? updateMutations[dataType] : null;

export const getPayrollDeleteMutation = (dataType?: string): DocumentNode | null =>
  isPayrollDataType(dataType) ? deleteMutations[dataType] : null;

export const getPayrollListQueryName = (dataType?: string): string | null =>
  isPayrollDataType(dataType) ? listQueryNames[dataType] : null;

export const getPayrollListRootField = (dataType?: string): string | null =>
  isPayrollDataType(dataType) ? listRootFields[dataType] : null;
